import React, { useState, useEffect } from "react";
import {Eye, X, Calendar, Download, CheckCircle, XCircle} from "lucide-react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { formatDateOnlyForDisplay, getColombiaDateString } from "../../../../../../utils/dateUtils";

const ManifiestoList = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  refreshTrigger,
  onManifiestoSelect
}) => {
  const [manifiestos, setManifiestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fecha_desde: null,
    fecha_hasta: null
  });

  useEffect(() => {
    loadManifiestos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusinessId, refreshTrigger, filters]);

  const loadManifiestos = async () => {
    setLoading(true);
    try {
      // Build filter object, only including date filters when both dates are valid
      const apiFilters = {
        limit: 100
      };

      // Only apply date filters if both dates are set and valid
      if (filters.fecha_desde && filters.fecha_hasta) {
        apiFilters.fecha_desde = filters.fecha_desde;
        apiFilters.fecha_hasta = filters.fecha_hasta;
      }

      const res = await roldanApi.getManifiestos(
        mantisBusinessId,
        token,
        apiFilters
      );
      if (res?.success) setManifiestos(res.data || []);
    } catch (error) {
      console.error("Error loading manifiestos:", error);
    } finally {
      setLoading(false);
    }
  };

  
  const exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = manifiestos.map((manifiesto) => ({
        "N° Manifiesto ERP": manifiesto.numero_manifiesto_erp || "N/A",
        "Pre-Manifiesto": manifiesto.numero_pre_manifiesto || "N/A",
        "Fecha Creación ERP": manifiesto.fecha_creacion_erp
          ? new Date(manifiesto.fecha_creacion_erp).toLocaleDateString("es-ES")
          : "N/A",
        Estado: manifiesto.estado || "N/A",
        "Autorización RNDC":
          manifiesto.autorizacion != null && String(manifiesto.autorizacion).trim() !== ""
            ? String(manifiesto.autorizacion).trim()
            : "No autorizado",
        "Cantidad Remesas": manifiesto.cantidad_remesas || 0,
        "Fecha Viaje": manifiesto.fecha_viaje
          ? formatDateOnlyForDisplay(manifiesto.fecha_viaje)
          : "N/A",
        "Respuesta ERP": manifiesto.respuesta_erp || "N/A"
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      ws["!cols"] = [
        {wch: 20}, // N° Manifiesto ERP
        {wch: 20}, // Pre-Manifiesto
        {wch: 18}, // Fecha Creación ERP
        {wch: 15}, // Estado
        {wch: 20}, // Autorización RNDC
        {wch: 18}, // Cantidad Remesas
        {wch: 15}, // Fecha Viaje
        {wch: 30} // Respuesta ERP
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Manifiestos");

      // Generate filename with date range if applicable
      const timestamp = getColombiaDateString();
      let filename = `Manifiestos_${timestamp}`;
      if (filters.fecha_desde && filters.fecha_hasta) {
        filename = `Manifiestos_${filters.fecha_desde}_${filters.fecha_hasta}`;
      }

      // Download file
      XLSX.writeFile(wb, `${filename}.xlsx`);

      alert(`Se exportaron ${manifiestos.length} manifiestos a Excel`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Error al exportar a Excel");
    }
  };

  if (loading) return <div className="loading">Cargando...</div>;

  const chipCls = (estado) => {
    const e = (estado || "").toLowerCase();
    if (e.includes("activo")) return "bg-emerald-100 text-emerald-700";
    if (e.includes("anulado")) return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-700";
  };

  return (
    <div className="px-2">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex items-end justify-between gap-4 p-4 border-b border-gray-100">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Manifiestos</h1>
            <p className="text-sm text-gray-500">
              Historial de manifiestos enviados al ERP
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              placeholder="Desde"
              className="bg-transparent outline-none text-sm"
              value={filters.fecha_desde || ""}
              onChange={(e) =>
                setFilters({...filters, fecha_desde: e.target.value || null})
              }
            />
            {filters.fecha_desde && (
              <button
                onClick={() => setFilters({...filters, fecha_desde: null})}
                className="text-gray-400 hover:text-gray-600"
                title="Limpiar fecha desde"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              placeholder="Hasta"
              className="bg-transparent outline-none text-sm"
              value={filters.fecha_hasta || ""}
              onChange={(e) =>
                setFilters({...filters, fecha_hasta: e.target.value || null})
              }
            />
            {filters.fecha_hasta && (
              <button
                onClick={() => setFilters({...filters, fecha_hasta: null})}
                className="text-gray-400 hover:text-gray-600"
                title="Limpiar fecha hasta"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            onClick={exportToExcel}
            disabled={manifiestos.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Exportar a Excel"
          >
            <Download size={16} /> Exportar Excel
          </button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-600">
                <th className="text-left font-semibold px-4 py-3">
                  N° Manifiesto ERP
                </th>
                <th className="text-left font-semibold px-4 py-3">
                  Pre-Manifiesto
                </th>
                <th className="text-left font-semibold px-4 py-3">
                  Fecha Creación
                </th>
                <th className="text-left font-semibold px-4 py-3">Estado</th>
                <th className="text-left font-semibold px-4 py-3">
                  Autorización RNDC
                </th>
                <th className="text-left font-semibold px-4 py-3">Remesas</th>
                <th className="text-left font-semibold px-4 py-3 w-28">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {manifiestos.map((m, idx) => (
                <tr
                  key={m.manifiesto_id}
                  className={idx % 2 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">
                    {m.numero_manifiesto_erp}
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-900">
                    {m.numero_pre_manifiesto}
                  </td>
                  <td className="px-4 py-3 text-gray-800">
                    {m.fecha_creacion_erp
                      ? new Date(m.fecha_creacion_erp).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full font-semibold ${chipCls(
                        m.estado
                      )}`}
                    >
                      {m.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {m.autorizacion != null && String(m.autorizacion).trim() !== "" ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-xs bg-emerald-100 text-emerald-700"
                        title="Manifiesto autorizado por RNDC"
                      >
                        <CheckCircle size={14} /> {String(m.autorizacion).trim()}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-xs bg-amber-100 text-amber-800"
                        title="Manifiesto sin autorización RNDC"
                      >
                        <XCircle size={14} /> No autorizado
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-800">
                    {m.cantidad_remesas || 0}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {onManifiestoSelect && (
                        <button
                          onClick={() => onManifiestoSelect(m.manifiesto_id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
                          title="Ver detalle"
                        >
                          <Eye size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManifiestoList;

